import React, { Suspense, useCallback, useEffect, useState } from 'react';
import type { WorkflowNodeInput } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Text } from '@cotera/client/app/components/ui';
import { resolveWorkflowNodeEditStrategy } from './node/edit-strategies';
import {
  resolveGraphNodeRenderableNodeByViewData,
  useWorkflowRenderCatalog,
} from '../workflow-render-catalog';
import { WorkflowEditPanel } from './workflow-edit-panel';
import type { WorkflowEditLayout } from './workflow-edit-layout';
import type { WorkflowNodeViewData } from './node';
import { asRecord, readString } from './utils';
import { useWorkflowAgentNodeState } from '../use-workflow-agent-node-state';
import { agentDescription, agentTitle } from './node/agent-node-display';
import { useWorkflowGraphMutations } from '../use-workflow-graph-mutations';
import { useWorkflowDrill } from '../workflow-drill-context';

/**
 * Config form for a node, hosted in the chat artifact panel. The header reuses
 * the node's render strategy (icon + name + provider/type) so it reads like the
 * card it was opened from; the body resolves a per-type editor from the
 * edit-strategy registry.
 *
 * Editors marked `autosave` on their strategy commit as the user types (like the
 * rest of the workflow editor) and so render no footer — there is nothing left
 * for Save to do and nothing for Cancel to discard. The rest keep the explicit
 * Cancel/Save pair, where both return to the workflow canvas artifact.
 */
export const WorkflowNodeEditDrawer: React.FC<{
  node: WorkflowNodeViewData | null;
  layout?: WorkflowEditLayout;
}> = ({ node, layout = 'panel' }) => {
  const { updateNodeData } = useWorkflowGraphMutations();
  const { close } = useWorkflowDrill();
  // Tag the draft with the id of the node it was produced for. The drawer is a
  // single reused instance (only the inner editor is keyed by node id), so when
  // the inspector target switches nodes this state survives the swap. Writing a
  // previous node's payload into the new node's id turns the new node into a
  // clone of the old one — the "add Write-to-Dataset, watch it flip into the
  // agent above it" bug. The tag lets every writer reject a draft that doesn't
  // belong to the current node.
  const [draft, setDraft] = useState<{
    nodeId: string;
    input: WorkflowNodeInput;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const catalog = useWorkflowRenderCatalog();
  const editStrategy =
    node !== null ? resolveWorkflowNodeEditStrategy(node.type) : null;
  const renderStrategy =
    node !== null
      ? resolveGraphNodeRenderableNodeByViewData(catalog, node).view
      : null;

  const agentId =
    node?.type === PersistedWorkflow.WorkflowNodeType.Agent
      ? readString(asRecord(node.data), 'agentId')
      : undefined;
  const { agent, meta: agentMeta } = useWorkflowAgentNodeState(agentId);

  const headerTitle = ((): string => {
    if (node === null) {
      return editStrategy?.title ?? 'Node';
    }
    if (node.type === PersistedWorkflow.WorkflowNodeType.Agent) {
      return agentTitle(node, agent, agentMeta);
    }
    if (renderStrategy !== null) {
      return renderStrategy.getTitle(node);
    }
    return editStrategy?.title ?? 'Node';
  })();

  const headerSubtitle = ((): string | undefined => {
    if (node === null) {
      return undefined;
    }
    if (node.type === PersistedWorkflow.WorkflowNodeType.Agent) {
      return agentDescription(node, agent, agentMeta);
    }
    return renderStrategy?.getDescription?.(node);
  })();
  const headerIcon =
    node !== null && renderStrategy !== null
      ? renderStrategy.renderIcon?.(node)
      : null;

  const autosave = editStrategy?.autosave === true;

  // Stable across renders for a given node — editors put `onChange` in their
  // report effect's deps (see tool-call-node-editor), so a fresh closure every
  // render would re-fire that effect endlessly and crash the panel. Keyed on the
  // node id so it only changes identity when the open node does, and stamps each
  // payload with that id so a stale draft can't be written into another node.
  const nodeId = node?.id;
  const handleDraftChange = useCallback(
    (input: WorkflowNodeInput | null) => {
      setDraft(
        input === null || nodeId === undefined ? null : { nodeId, input }
      );
    },
    [nodeId]
  );

  // The payload only counts once it belongs to the node currently open. A draft
  // left over from a previously-open node reads as "nothing to save" here, so
  // neither the autosave effect nor Save can leak it into this node.
  const currentDraft =
    draft !== null && node !== null && draft.nodeId === node.id
      ? draft.input
      : null;

  // Autosaving editors commit straight into the draft graph — no debounce of
  // their own. The workflow's own autosave already re-arms on every
  // `graphWatchable` change, so the server write still lands once the user
  // pauses, and the canvas reflects the edit immediately instead of a debounce
  // later. Editors report `null` while incomplete, so nothing lands until the
  // payload goes valid again; `draftUpdateNodeData` no-ops on an unchanged
  // payload, which is what keeps the editors' mount-time report from marking
  // the workflow dirty.
  useEffect(() => {
    if (!autosave || node === null || currentDraft === null) {
      return;
    }
    void updateNodeData(node.id, currentDraft);
  }, [autosave, node, currentDraft, updateNodeData]);

  const handleSave = async (): Promise<void> => {
    if (node === null || currentDraft === null) {
      return;
    }
    setSaving(true);
    await updateNodeData(node.id, currentDraft);
    setSaving(false);
    close();
  };

  const editorBody =
    node !== null && editStrategy !== null ? (
      <Suspense
        fallback={
          <Text.Caption className="text-muted-foreground block">
            Loading…
          </Text.Caption>
        }
      >
        <editStrategy.Editor
          key={node.id}
          node={node}
          onChange={handleDraftChange}
        />
      </Suspense>
    ) : (
      <Text.Caption className="text-muted-foreground block">
        This node type has no editable configuration yet.
      </Text.Caption>
    );

  const footer =
    editStrategy !== null && !autosave ? (
      <>
        <Button variant="outline" onClick={close}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSave()}
          disabled={currentDraft === null || saving}
        >
          Save changes
        </Button>
      </>
    ) : null;

  if (layout === 'page') {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
        {/* overflow-x-hidden: `overflow-y-auto` alone forces overflow-x from
            `visible` to `auto`, spawning a spurious horizontal scrollbar. */}
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl px-5 py-6">{editorBody}</div>
        </div>
        {footer !== null ? (
          <div className="shrink-0 border-t border-border/50 bg-background px-5 py-3">
            <div className="mx-auto flex w-full max-w-5xl justify-end gap-2">
              {footer}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <WorkflowEditPanel
      open={node !== null}
      // The inspector floats over the canvas with no chrome of its own, so the
      // header owns dismissal there. In the artifact panel it must not — that
      // panel already has a close button.
      {...(layout === 'inspector' ? { onClose: close } : {})}
      icon={headerIcon}
      accent={renderStrategy?.accent}
      badge={renderStrategy?.badge}
      title={headerTitle}
      subtitle={headerSubtitle}
      footer={footer}
    >
      {editorBody}
    </WorkflowEditPanel>
  );
};
